<script lang="ts" setup>
import type {Area} from "#magnolia-layer/types/magnolia-types";
import type {Delivery} from "#magnolia-layer/types/api";
import type {Page} from "~/types/mgnl";
import {PhArrowRight} from "@phosphor-icons/vue";

const props = defineProps<{
  metadata: Area;
  title: string;
  pageRootLink: string;
}>();

const pageIsLight = inject<boolean>("pageIsLight");
const twClasses = !pageIsLight ? "bg-white text-black has-glow" : "";

const offset = 0;
const limit = 3;

const runtimeConfig = useRuntimeConfig();
const context = useMagnoliaContext();
const {data: content, error} = await useFetch<Delivery<Page>>(
  "/api/paginatedChildPages",
  {
    params: {
      deliveryApi: runtimeConfig.public.mgnl.apiPageOverview,
      offset: offset,
      limit: limit,
      path: props.pageRootLink,
      lang: context.searchParams?.lang,
    },
  },
);

const getPageLink = (path: string): string | undefined => {
  if (path) {
    return stripPrefix(path);
  }
  return undefined;
};
</script>

<template>
  <section :class="[twClasses]" class="col-span-full content-grid py-22">
    <h1 class="text-5xl lg:text-8xl mb-22">
      {{ title }}
    </h1>
    <div class="articles flex flex-col lg:flex-row gap-6 items-end">
      <template v-for="(page, index) in content?.results" :key="index">
        <article
          class="min-w-full lg:min-w-0 flex flex-col gap-x-4 flex-2 mb-16">
          <NuxtLinkLocale
            v-if="getPageLink(page['@path'])"
            :href="getPageLink(page['@path'])"
            class="text-[inherit] block">
            <MagnoliaPicture
              v-if="page.image"
              :asset="page.image"
              :modifiers="{grayscale: 1}"
              densities="1x 2x"
              loading="lazy"
              class="*:w-full *:h-full *:object-contain *:object-center lg:*:w-auto"
              sizes="400 md:600 lg:520 xl:640">
            </MagnoliaPicture>
          </NuxtLinkLocale>
          <div class="article__body">
            <h1 class="uppercase font-sans font-bold mt-4 mb-2">
              <NuxtLinkLocale
                v-if="getPageLink(page['@path'])"
                :href="getPageLink(page['@path'])"
                class="text-[inherit]">
                {{ page.title }}
              </NuxtLinkLocale>
            </h1>
            <p v-if="page.abstract" class="text-width-fit">
              {{ page.abstract }}
            </p>
          </div>

          <NuxtLinkLocale
            v-if="getPageLink(page['@path'])"
            :href="getPageLink(page['@path'])"
            class="link-with-icon link-underline mt-4 text-sunrise-200 mr-auto">
            {{ $t("label.continueReading") }}
            <PhArrowRight></PhArrowRight>
          </NuxtLinkLocale>
        </article>
      </template>
    </div>
  </section>
</template>

<style lang="scss" scoped>
p {
  overflow: hidden;
  display: block;
  display: -webkit-box;
  text-overflow: ellipsis;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}
</style>
